import { palette } from "./tokens";

/**
 * Chili icon, drawn rather than an emoji so it renders identically on every
 * phone — the same reason the side markers and faces are SVG.
 *
 * Spice is 1–3, shown as a count of chilis rather than a number: it reads at a
 * glance, needs no label, and survives being shrunk into a row of buttons.
 */

const HOT = "#C03A28";
const STEM = "#6F8C3F";

export const Chili = ({
  size = 20,
  active = true,
}: {
  size?: number;
  /** Inactive chilis stay visible as outlines, so the scale is always legible. */
  active?: boolean;
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    {/* Stem, angled off the shoulder. */}
    <path
      d="M12.2 7.2c-.4-1.6-.2-2.9.7-3.8"
      fill="none"
      stroke={active ? STEM : palette.inkDim}
      strokeWidth="1.8"
      strokeLinecap="round"
      opacity={active ? 1 : 0.45}
    />
    <path
      d="M12.9 3.4c1.1-.5 2.2-.4 3.2.3"
      fill="none"
      stroke={active ? STEM : palette.inkDim}
      strokeWidth="1.8"
      strokeLinecap="round"
      opacity={active ? 1 : 0.45}
    />

    {/* Body: wide at the shoulder, curving to a point. */}
    <path
      d="M12.2 7c3.6.6 5.6 3.4 5.1 6.9-.5 3.6-3.1 6.7-5.6 8.1-.5.3-1.1-.2-.9-.8.7-2.1 1.1-4 1-5.7-.1-2.4-1-4.3-2.4-5.9-.6-.7-.1-1.8.8-1.9.7-.1 1.3-.4 2-.7z"
      fill={active ? HOT : "none"}
      stroke={active ? "none" : palette.inkDim}
      strokeWidth="1.6"
      opacity={active ? 1 : 0.45}
    />
  </svg>
);

/**
 * A row of three, `level` of them filled.
 *
 * `gap` may be negative, which overlaps them slightly — that is what makes two
 * and three read as one cluster rather than a count to be tallied.
 */
export const ChiliRow = ({
  level,
  size = 20,
  showEmpty = true,
  gap = 2,
}: {
  level: 1 | 2 | 3;
  size?: number;
  showEmpty?: boolean;
  gap?: number;
}) => (
  <span
    className="inline-flex items-center"
    role="img"
    aria-label={`Spice level ${level} of 3`}
    style={{ gap }}
  >
    {[1, 2, 3].map((i) =>
      i <= level ? (
        <Chili key={i} size={size} />
      ) : showEmpty ? (
        <Chili key={i} size={size} active={false} />
      ) : null,
    )}
  </span>
);
